import type {
    IGenerationAttempt,
    IGenerationObservability,
    IGenerationProvider,
    IGenerationSummary
} from '@newo/api/gateway';

export interface GenerationGroup {
    id: string;
    summary: IGenerationSummary | undefined;
    attempts: IGenerationAttempt[];
}

const UNKNOWN = 'unknown';
export const SEPARATOR = ' · ';
export const ARROW = ' → ';

const joinParts = (parts: (string | undefined)[]) =>
    parts.filter(Boolean).join(SEPARATOR);

const humanize = (value: string) => value.replaceAll('_', ' ');

const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
    `${count} ${count === 1 ? singular : plural}`;

const formatModelChain = (models: (string | null | undefined)[]) =>
    models.every((model) => model === null || model === undefined)
        ? UNKNOWN
        : models.map((model) => model ?? UNKNOWN).join(ARROW);

export const formatMs = (ms: number | null | undefined) =>
    ms === null || ms === undefined ? UNKNOWN : `${ms} ms`;

// A missing count is shown as unknown so that it never reads as zero.
export const formatRetries = (count: number | null | undefined) =>
    count === null || count === undefined
        ? `retries ${UNKNOWN}`
        : pluralize(count, 'retry', 'retries');

export const formatProvider = (provider: IGenerationProvider | null) => {
    if (provider === null) {
        return UNKNOWN;
    }
    return joinParts([
        provider.outcome === null ? UNKNOWN : humanize(provider.outcome),
        formatRetries(provider.total_retry_count)
    ]);
};

export const formatProviderModels = (provider: IGenerationProvider | null) =>
    formatModelChain([
        provider?.selected_model,
        provider?.effective_model,
        provider?.actual_model
    ]);

export const formatGenerationSummary = (
    summary: IGenerationSummary | undefined
) =>
    summary
        ? joinParts([
              humanize(summary.outcome),
              pluralize(summary.attempt_count, 'attempt'),
              pluralize(summary.call_round_count, 'MCP round'),
              formatRetries(summary.retry_count),
              formatMs(summary.time_ms)
          ])
        : 'no summary yet';

export const formatAttempt = (attempt: IGenerationAttempt) =>
    joinParts([
        `outer call ${attempt.outer_call_number}`,
        `MCP round ${attempt.call_round}`,
        `attempt ${attempt.attempt_number}/${attempt.max_attempts}`,
        humanize(attempt.status)
    ]);

export const formatAttemptModels = (attempt: IGenerationAttempt) =>
    joinParts([
        formatModelChain([
            attempt.selected_model,
            attempt.effective_model,
            attempt.actual_model
        ]),
        attempt.model_transition_reason
            ? humanize(attempt.model_transition_reason)
            : undefined
    ]);

export const formatTokens = (attempt: IGenerationAttempt) =>
    joinParts([
        `${attempt.input_tokens_cached ?? UNKNOWN} cached`,
        `${attempt.input_tokens_uncached ?? UNKNOWN} uncached`,
        `${attempt.output_tokens ?? UNKNOWN} output`
    ]);

export const formatCost = (attempt: IGenerationAttempt) =>
    joinParts([attempt.cost ?? UNKNOWN, `${attempt.tier} tier`]);

// Attempts can arrive before their generation summary, so the groups come from
// both sources rather than from the summaries alone.
export const groupByGeneration = (
    observability: IGenerationObservability
): GenerationGroup[] => {
    const ids = new Set([
        ...observability.generations.map((summary) => summary.generation_id),
        ...observability.attempts.map((attempt) => attempt.generation_id)
    ]);
    return Array.from(ids, (id) => ({
        id,
        summary: observability.generations.find(
            (summary) => summary.generation_id === id
        ),
        attempts: observability.attempts.filter(
            (attempt) => attempt.generation_id === id
        )
    }));
};
